import { useCallback, useState } from "react";

export interface SmartViewSnapshot<Value> {
  versionKey: string;
  value: Value;
}

interface UseSmartViewSnapshotsOptions {
  /**
   * When true, the next snapshot pushed will be deferred into the pending slot
   * instead of immediately replacing the current snapshot.
   */
  defer?: boolean;
}

interface SmartViewSnapshotsState<Value> {
  current: SmartViewSnapshot<Value> | null;
  pending: SmartViewSnapshot<Value> | null;
}

interface UseSmartViewSnapshotsResult<Value> extends SmartViewSnapshotsState<Value> {
  pushSnapshot: (
    snapshot: SmartViewSnapshot<Value>,
    options?: UseSmartViewSnapshotsOptions
  ) => void;
  acceptPending: () => void;
  resetSnapshots: () => void;
}

export function useSmartViewSnapshots<Value>(): UseSmartViewSnapshotsResult<Value> {
  const [state, setState] = useState<SmartViewSnapshotsState<Value>>({
    current: null,
    pending: null,
  });

  const pushSnapshot = useCallback(
    (snapshot: SmartViewSnapshot<Value>, options: UseSmartViewSnapshotsOptions = {}) => {
      setState(prev => {
        const { current } = prev;

        if (current && current.versionKey === snapshot.versionKey) {
          return prev;
        }

        if (options.defer) {
          return {
            current,
            pending: snapshot,
          };
        }

        return {
          current: snapshot,
          pending: null,
        };
      });
    },
    []
  );

  const acceptPending = useCallback(() => {
    setState(prev => {
      if (!prev.pending) {
        return prev;
      }

      return {
        current: prev.pending,
        pending: null,
      };
    });
  }, []);

  const resetSnapshots = useCallback(() => {
    setState({ current: null, pending: null });
  }, []);

  return {
    current: state.current,
    pending: state.pending,
    pushSnapshot,
    acceptPending,
    resetSnapshots,
  };
}
